import { useRef, useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { PickSelect } from "@/components/apply/Field";
import { Panel } from "@/components/admin/Panel";
import { api, formatErr } from "@/lib/api";

const MANUAL = ["New", "ID Under Review", "Interview Scheduled", "Interviewed", "Approved", "Declined"];

export const VettingPanel = ({ app, limit, onSaved }) => {
  const [score, setScore] = useState(app.vetting_score || 5);
  const [limitVal, setLimitVal] = useState(limit);
  const timer = useRef(null);
  const commitScore = ([v]) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => patch({ vetting_score: v }, "Score saved"), 600);
  };

  const patch = async (body, msg) => {
    try {
      const { data } = await api.patch(`/admin/applications/${app.id}`, body);
      onSaved(data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const locked = ["Cash Payment Received", "Active Member", "Suspended"].includes(app.status);
  return (
    <Panel title="Vetting" testId="panel-vetting">
      <div className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm font-bold">Status</p>
          {locked ? <p className="text-sm text-muted-foreground">Membership active. Status is locked.</p> : (
            <PickSelect value={app.status} onChange={(v) => patch({ status: v }, v === "Approved" || v === "Declined" ? `${v} - email sent (mocked)` : "Status updated")} options={MANUAL} testId="vetting-status-select" />
          )}
        </div>
        <div className="space-y-3">
          <div className="flex justify-between text-sm font-bold"><span>Vetting score</span><span data-testid="vetting-score-value" className="text-primary">{score}/10</span></div>
          <Slider min={1} max={10} step={1} value={[score]} onValueChange={([v]) => setScore(v)} onValueCommit={commitScore} data-testid="vetting-score-slider" />
        </div>
        <label className="flex items-center justify-between rounded-xl border border-border p-3 cursor-pointer">
          <span className="flex items-center gap-2 font-bold text-sm"><Flag className={`h-4 w-4 ${app.red_flag ? "text-red-500 fill-red-500" : ""}`} /> Red flag</span>
          <Switch checked={!!app.red_flag} onCheckedChange={(v) => patch({ red_flag: v }, v ? "Flagged" : "Flag removed")} data-testid="vetting-red-flag-toggle" />
        </label>
        <div className="space-y-2">
          <p className="text-sm font-bold">Introduction limit</p>
          <div className="flex gap-2">
            <Input type="number" min={1} value={limitVal} onChange={(e) => setLimitVal(e.target.value)} className="h-10 rounded-xl" data-testid="vetting-intro-limit-input" />
            <button onClick={() => patch({ intro_limit: Number(limitVal) }, "Limit saved")} className="h-10 px-4 rounded-full border border-border text-sm font-semibold hover:bg-secondary" data-testid="vetting-intro-limit-save">Save</button>
          </div>
          <p className="text-xs text-muted-foreground">Defaults to the tier limit. Raise it for Platinum (5+) members.</p>
        </div>
      </div>
    </Panel>
  );
};
